/**
 * Color Mapping Configuration
 * Maps color names to their corresponding hex values for facet display
 * Uses global window.Swatches.settings from Shopify settings when available
 * 
 * @version 1.0.0
 */

/**
 * Get color mappings from global window.Swatches.settings
 * @returns {Object} - Color mapping object
 */
function getGlobalColorMap() {
  // Return global swatches settings or empty object
  if (typeof window !== 'undefined' && 
      window.Swatches && 
      window.Swatches.settings && 
      typeof window.Swatches.settings === 'object') {
    return window.Swatches.settings;
  }
  
  return {};
}



/**
 * Dynamic color mapping that uses global Shopify settings
 */
export const COLOR_MAP = new Proxy({}, {
  get(target, prop) {
    const globalColors = getGlobalColorMap();
    return globalColors[prop];
  },
  
  has(target, prop) {
    const globalColors = getGlobalColorMap();
    return prop in globalColors;
  },
  
  ownKeys(target) {
    const globalColors = getGlobalColorMap();
    return Object.keys(globalColors);
  },
  
  getOwnPropertyDescriptor(target, prop) {
    const globalColors = getGlobalColorMap();
    if (prop in globalColors) {
      return {
        enumerable: true,
        configurable: true,
        value: globalColors[prop]
      };
    }
    return undefined;
  }
});

/**
 * Default fallback color for unmapped color names
 */
export const DEFAULT_COLOR = '#cccccc';

/**
 * Configuration options for color mapping behavior
 */
export const COLOR_CONFIG = {
  // Whether to normalize color names (lowercase, trim whitespace)
  normalizeNames: true,
  
  // Default color to use when no match is found
  fallbackColor: DEFAULT_COLOR,
  
  // Whether to log warnings for unmapped colors (useful for development)
  logUnmappedColors: process.env.NODE_ENV === 'development',
};

/**
 * Get color value for a given color name
 * @param {string} colorName - The color name to look up
 * @param {Object} options - Optional configuration overrides
 * @returns {string} - The hex color value or fallback color
 */
export function getColorValue(colorName, options = {}) {
  const config = { ...COLOR_CONFIG, ...options };
  
  if (!colorName || typeof colorName !== 'string') {
    return config.fallbackColor;
  }
  
  // Normalize the color name if configured to do so
  const normalizedName = config.normalizeNames
    ? colorName.toLowerCase().trim()
    : colorName;
  
  // Get the current color mapping from global swatches
  const currentColorMap = getGlobalColorMap();
  
  // Look up the color in the mapping
  const colorValue = currentColorMap[normalizedName];
  
  if (!colorValue) {
    if (config.logUnmappedColors) {
      console.warn(`Unmapped color name: "${colorName}" (normalized: "${normalizedName}")`);
    }
    return config.fallbackColor;
  }
  
  return colorValue;
}

/**
 * Check if a color name exists in the mapping
 * @param {string} colorName - The color name to check
 * @returns {boolean} - True if the color exists in the mapping
 */
export function hasColor(colorName) {
  if (!colorName || typeof colorName !== 'string') {
    return false;
  }
  
  const normalizedName = COLOR_CONFIG.normalizeNames
    ? colorName.toLowerCase().trim()
    : colorName;
    
  const currentColorMap = getGlobalColorMap();
  return normalizedName in currentColorMap;
}

/**
 * Get all available color names
 * @returns {string[]} - Array of all color names in the mapping
 */
export function getAvailableColorNames() {
  const currentColorMap = getGlobalColorMap();
  return Object.keys(currentColorMap);
}

export default {
  COLOR_MAP,
  DEFAULT_COLOR,
  COLOR_CONFIG,
  getColorValue,
  hasColor,
  getAvailableColorNames,
};